import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const PRIMARY = '#29204C';
const SECONDARY = '#5A6D8B';
const SUCCESS = '#68B977';

/**
 * ModernTable Component - Reusable table with consistent Material-UI design
 *
 * @param {Array} columns - Array of column definitions
 * @param {Array} data - Array of data rows
 * @param {Boolean} loading - Loading state
 * @param {Number} page - Current page (0-indexed)
 * @param {Number} rowsPerPage - Rows per page
 * @param {Number} totalRows - Total number of rows
 * @param {Function} onPageChange - Page change handler
 * @param {Function} onRowsPerPageChange - Rows per page change handler
 * @param {Function} onView - View action handler
 * @param {Function} onEdit - Edit action handler
 * @param {Function} onDelete - Delete action handler
 * @param {String} emptyMessage - Message to show when no data
 */
const ModernTable = ({
  columns = [],
  data = [],
  loading = false,
  page = 0,
  rowsPerPage = 15,
  totalRows = 0,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
  emptyMessage = 'No data available',
  showActions = true,
}) => {
  const handleChangePage = (event, newPage) => {
    if (onPageChange) onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (onRowsPerPageChange) onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: PRIMARY }}>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  align={column.align || 'left'}
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    py: 2,
                    minWidth: column.minWidth || 'auto',
                    width: column.width || 'auto',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {showActions && (
                <TableCell
                  align="center"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    py: 2,
                    width: 150,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (showActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  sx={{
                    bgcolor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                    '&:hover': {
                      bgcolor: '#f3f6fa',
                    },
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={colIndex}
                      align={column.align || 'left'}
                      sx={{ py: 1.5, fontSize: '0.875rem' }}
                    >
                      {column.render ? column.render(row) : (row[column.field] ?? 'N/A')}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {onView && (
                          <IconButton
                            size="small"
                            onClick={() => onView(row)}
                            sx={{
                              color: '#1976d2',
                              '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' },
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        )}
                        {onEdit && (
                          <IconButton
                            size="small"
                            onClick={() => onEdit(row)}
                            sx={{
                              color: '#ed6c02',
                              '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.08)' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {onDelete && (
                          <IconButton
                            size="small"
                            onClick={() => onDelete(row)}
                            sx={{
                              color: '#d32f2f',
                              '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data.length > 0 && (
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #e0e0e0',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            },
          }}
        />
      )}
    </Paper>
  );
};

export default ModernTable;
